import { VariantProps } from 'class-variance-authority';

import { CloseIcon } from '@/assets/icons';
import { Skeleton, SvgIcon } from '@/components/common';
import { mergeClasses } from '@/utils';

import { getFileIcon } from './FileItem.utils';
import { FILE_ITEM_VARIANTS } from './FileItem.constants';

interface IFileItem
  extends VariantProps<typeof FILE_ITEM_VARIANTS>,
    React.HtmlHTMLAttributes<HTMLDivElement> {
  file: IFile;
  isLoading?: boolean;
  error?: string;
  disabled?: boolean;
  isGlass?: boolean;
  canClear?: boolean;
  onClear?: (fileName: string) => void;
}

interface IFile {
  name: string;
  type: string;
}

const FileItem = ({
  variant = 'default',
  file,
  error,
  disabled,
  className,
  isLoading,
  isGlass,
  canClear,
  onClear,
  ...props
}: IFileItem) => {
  const iconComponent = getFileIcon(file.name, file.type);
  return (
    <div className={mergeClasses('flex flex-col gap-2 w-fit max-w-full', className)} {...props}>
      <div
        className={mergeClasses(
          FILE_ITEM_VARIANTS({ variant }),
          isGlass
            ? 'bg-opacity-dark-5'
            : 'bg-neutral-2 dark:bg-neutral-9 border border-neutral-3 dark:border-neutral-8',
          error && '!border-semantic-error-base',
        )}
        style={
          isGlass
            ? {
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                boxShadow:
                  'inset 0 4px 8px -4px rgba(0,0,0,0.09), inset 0 -1px 1px 0 rgba(255,255,255,0.9), inset 0 1px 1px 0 rgba(255,255,255,0.9)',
              }
            : undefined
        }
      >
        {isLoading ? (
          <div data-slot="file-item-loading" className="flex flex-col gap-2">
            <Skeleton data-slot="file-item-skeleton" className="w-20 rounded-xs" />
            <Skeleton data-slot="file-item-skeleton" className="w-[60px] rounded-xs" />
          </div>
        ) : (
          <>
            <SvgIcon data-slot="file-item-icon" component={iconComponent} />
            <div data-slot="file-item-name" className="font-semibold truncate">
              {file.name}
            </div>
          </>
        )}
        {canClear && (
          <button
            data-slot="file-item-clear"
            className={mergeClasses(
              'min-h-0 p-0 rounded-full bg-neutral-4 text-neutral-7 dark:bg-neutral-8 dark:text-neutral-6 flex justify-center items-center border border-neutral-5 dark:border-neutral-7',
              disabled ? 'cursor-not-allowed' : 'hover:bg-neutral-5',
            )}
            onClick={(event) => {
              event.stopPropagation();
              onClear?.(file.name);
            }}
            disabled={disabled}
          >
            <SvgIcon data-slot="file-item-clear-icon" component={CloseIcon} className="size-4" />
          </button>
        )}
      </div>
      {error && <p className="px-3 text-xs font-medium text-semantic-error-base">{error}</p>}
    </div>
  );
};

export { FileItem, type IFile };
